import createPersistedState from 'vuex-persistedstate';

import config from './config';
import overlays from './overlays';
import auth from './auth';
import flags from './flags';
import suggestions from './suggestions';

import { logger } from '@/shared/logging';
import cloneDeep from 'lodash/cloneDeep';
import { PlayStore, State } from '@/store/play-root-state';
import { createStore } from 'vuex';
import { env } from '@/env';
import { localStoreKey } from '@/shared/shared.utils';

// @todo Remove jwt from persistent path
const vuexPersistPaths = [
    'auth.session.jwt',
    'auth.hasEverLoggedIn',
    'auth.user',
    'auth.session.coreAppId',
    'auth.session.coachId',
    'flags',
    'suggestions.latestSuggestions',
];

if (!env.isProd) {
    vuexPersistPaths.push('config.localConfig');
}

const log = logger('store');
const persistedKey = localStoreKey('vuex');

// Flag to prevent subscriber from firing during rehydration
// Start as TRUE to block any mutations that fire during store creation/restoration
let isRehydrating = true;

/**
 * We lazy initialize the vuex store
 */

function createPlayStore() {
    return createStore<State>({
        getters: {
            allState: (state) => state,
        },
        modules: {
            overlays,
            auth,
            flags,
            config,
            suggestions,
        },
        plugins: [
            (store: PlayStore) => store.subscribe((mutation, state) => {
                // Don't trigger during rehydration to prevent race conditions
                if (isRehydrating) {
                    return null;
                }

                const { jwt } = state.auth.session;

                log.debug('Checking user details', mutation, state);

                if (jwt && !state.auth.accounts && !state.auth.userDetailsFetched) {
                    log.info('Loading user details:', mutation, state);

                    return store.dispatch('auth/FETCH_USER_DETAILS');
                }

                return null;
            }),

            createPersistedState({
                key: persistedKey,
                paths: vuexPersistPaths,
                rehydrated: (store) => {
                    // Keep flag as true throughout rehydration
                    // It was initialized as true to block any mutations during store creation
                    isRehydrating = true;
                    // @ts-ignore The old version of vuex doesn't do this properly
                    const { state } = store;

                    if (!env.isProd) {
                        log.info('Merging persisted values', {
                            ...cloneDeep(state.config),
                        });
                        // @ts-ignore Not sure why this doesn't work
                        store.commit('config/MERGE_CONFIG');
                        log.info('Merged config', cloneDeep(state.config.values));
                    }

                    // Fetch feature flags if JWT is available
                    // If no JWT yet, feature flags will be fetched later when JWT is obtained
                    const { jwt } = state.auth.session;

                    if (jwt) {
                        // Fetch fresh flags from server (async)
                        // This ensures we get the latest values but don't delay app initialization
                        // @ts-ignore - Store type doesn't include dispatch method in rehydrated callback
                        store.dispatch('flags/FETCH_FEATURE_FLAGS', true).catch((error) => {
                            log.error('Failed to fetch feature flags during rehydration', error);
                        });

                        // accounts/roles aren't persisted; fetch them here since the
                        // usual mutation subscriber is muted during rehydration.
                        // @ts-ignore - Store type doesn't include a dispatch method in rehydrated callback
                        store.dispatch('auth/FETCH_USER_DETAILS').catch((error) => {
                            log.error('Failed to fetch user details during rehydration', error);
                        });
                    } else {
                        log.warn('No JWT available yet - feature flags will be fetched after authentication');
                    }

                    // Rehydration complete, allow subscriber to fire normally
                    isRehydrating = false;
                },
            }),
        ],
    });
}

export const store = createPlayStore();

export default {
    store,
};
